import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { SearchbarCustomEvent } from "../../../../../shared/ion-components-events.interface";
import {
  GoogleMapsAutocompleteResult,
  GoogleMapsPlace,
  GoogleMapsService,
} from "src/services/google-maps/google-maps.service";
import { LoadingController } from "@ionic/angular";
import { AuthService } from "src/services/auth/auth.service";
import { User } from "src/services/user/user.interface";
import { BaseFormObjectComponent } from "../base-form-object.component";
import { AbstractControl, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { locationFormFields } from "./location.form";
import { OrderAddress } from "src/services/order/order.interface";
import { LatLng } from "src/services/subscription/base-location-lat-lng.const";

@Component({
  selector: "rc-location-form-field",
  templateUrl: "./location-form-field.component.html",
  styleUrls: ["./location-form-field.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: LocationFormFieldComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: LocationFormFieldComponent,
    },
  ],
  standalone: false,
})
export class LocationFormFieldComponent extends BaseFormObjectComponent<OrderAddress> implements OnInit {
  @Input() public initialAddress: OrderAddress | undefined;
  @Input() public defaultMapCenter: LatLng | undefined;
  @Input() public showMap: boolean = true;
  @Input() public zoom: number = 12;
  @Input() public selectedZoom: number | undefined;
  @Input() public disablePinchZoom: boolean = false;
  @Output() onSearchChange: EventEmitter<OrderAddress> = new EventEmitter<OrderAddress>();

  public currentUser: User | undefined;
  public form: FormGroup = this.formBuilder.group({
    [locationFormFields.apartment]: ["", []],
    [locationFormFields.country]: ["", []],
    [locationFormFields.formatted_address]: ["", [(control: AbstractControl) => Validators.required(control)]],
    [locationFormFields.id]: ["", []],
    [locationFormFields.lat]: ["", []],
    [locationFormFields.lng]: ["", []],
    [locationFormFields.name]: ["", []],
    [locationFormFields.placeId]: ["", []],
    [locationFormFields.street]: ["", []],
    [locationFormFields.state]: ["", []],
    [locationFormFields.zipCode]: ["", []],
  });
  public searchResults: GoogleMapsAutocompleteResult[] = [];

  constructor(
    private readonly authService: AuthService,
    private readonly formBuilder: FormBuilder,
    private readonly googleMapsService: GoogleMapsService,
    private readonly loadingController: LoadingController,
  ) {
    super();
  }

  protected get mapZoom(): number {
    const hasSelectedLocation =
      !!this.form.controls[locationFormFields.lat].value && !!this.form.controls[locationFormFields.lng].value;

    return hasSelectedLocation ? (this.selectedZoom ?? this.zoom) : this.zoom;
  }

  public async ngOnInit(): Promise<void> {
    if (this.initialAddress) {
      await this.writeValue(this.initialAddress);
    }
    this.currentUser = await this.authService.getActiveUser();
  }

  public async searchInputChanged(event: unknown): Promise<void> {
    const eventCast = event as SearchbarCustomEvent;
    const queryString = eventCast.detail.value;

    if (!queryString) {
      this.searchResults = [];
      return;
    }

    const result = await this.googleMapsService.autocomplete(queryString);
    this.searchResults = result?.slice(0, 3);
  }

  public async selectSearchResult(item: GoogleMapsAutocompleteResult): Promise<void> {
    this.searchResults = [];

    const loading = await this.loadingController.create();
    await loading.present();

    this.processPlace(item.place_id, async () => loading.dismiss());
  }

  public clearSearch(): void {
    this.form.reset();
  }

  private processPlace(placeId: string, onFinish?: () => Promise<unknown>): void {
    this.googleMapsService.getPlaceDetails(placeId, async (place: GoogleMapsPlace) => {
      place.place_id ??= placeId;
      const address = this.googleMapsService.placeToAddress(place);
      this.form.patchValue(address);
      this.onSearchChange.emit(address);

      await onFinish?.();
    });
  }

  protected toDisplayValue(_input: unknown): string {
    return "";
  }

  public validate(): object | null {
    if (this.form.valid) {
      return null;
    }

    return this.getAllFormErrors();
  }

  public readonly locationFormFields = locationFormFields;
}
